import React, { useState, useEffect } from "react";
import {
  Box, Typography, Tabs, Tab, Divider, Accordion, AccordionSummary,
  AccordionDetails, IconButton, Grid2,
} from "@mui/material";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import ChartCalendarV2 from "../../FleetPerformance/Components/ChartCalendarV2";
import { PrimaryGranularityButton } from "../../../Theme/DigitalTwinStyles";
import LocationFilter from "../../BmsFaults/Analytics/DetailsFilters/LocationFilter";
import GlobalFilter from "../../Overview/components/GlobalFilter";
import OperationalStatusFilter from "../../Common/OperationalStatusFilter";
import ProjectFilter from "../../BmsFaults/Analytics/DetailsFilters/ProjectFilter";
import EntityAffectedFilter from "../../BmsFaults/Analytics/DetailsFilters/EntityAffectedFilter";
import { useCanUseGlobalAdvancedFilter } from "../../../Helper/Permissions/GlobalAdvancedFilter";
import AppCache from "../../../Helper/Common/AppCache";
import CustomIcon from "../../../Components/CustomIcon";
import { FilterIconSvg } from "../../../Features/Table/Icons";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import SimpleCheckboxFilter from "../../../Components/SimpleCheckboxFilter";
import { analyticsCalendarEndDate } from "../../../Helper/DatePicker/DateConverter";
import { getFilterData } from "../../../Api/Api";
import { CHART_TYPES } from "./chartConfig";
import CellChemistryFilter from "../../FleetPerformance/Components/Filters/CellChemistryFilter";

const GRANULARITY_OPTIONS = ["Daily", "Weekly", "Monthly"];


const SmartAlertsDetailsSidebar = ({
  isExpanded,
  onToggle,
  chartType,
  startDate,
  endDate,
  onDateChange,
  granularity,
  onGranularityChange,
  allFleets,
  allOnboardingLocationsHierarchy,
  selectedFleetIds,
  selectedHierarchicalLocation,
  initialAlertCategories,
  initialOperationalStatus,
  onFiltersChange,
  onHierarchicalLocationChange,
  onFleetIdsChange,
  onAlertCategoriesChange,
  onOperationalStatusChange,
}) => {
  const canUseGlobalAdvancedFilter = useCanUseGlobalAdvancedFilter();
  const globalFilterConfig = AppCache.getItem("globalFilter")?.find((f) => f.type === "unifiedGlobalFilter") || null;
  const [activeTab, setActiveTab] = useState(0);

  const filtersEffectMounted = React.useRef(false);

  const initialFiltersRef = React.useRef({
    hierarchicalLocation: selectedHierarchicalLocation || [],
    fleetIds: selectedFleetIds || [],
    alertCategories: initialAlertCategories || [],
    operationalStatus: initialOperationalStatus || [],
  });

  const [selectedOperationalStatus, setSelectedOperationalStatus] = useState(initialOperationalStatus || []);
  const [selectedCategories, setSelectedCategories] = useState(initialAlertCategories || []);
  const [selectedAlertNames, setSelectedAlertNames] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [selectedCellChemistries, setSelectedCellChemistries] = useState([]);

  const [alertCategoryOptions, setAlertCategoryOptions] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [alertNameOptions, setAlertNameOptions] = useState([]);
  const [isLoadingAlertNames, setIsLoadingAlertNames] = useState(true);
  const [selectedResolutionDurations, setSelectedResolutionDurations] = useState([]);
  const [resolutionDurationOptions, setResolutionDurationOptions] = useState([]);
  const [isLoadingDurations, setIsLoadingDurations] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const filterConfig = {
          type: "dynamic",
          queryParams: { table: "alertMeta", column: "alertType" },
        };
        const res = await getFilterData(filterConfig);
        const list = res?.response?.response?.filterData || [];
        setAlertCategoryOptions(list.map((c) => c.id));
      } catch {
        setAlertCategoryOptions([]);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchAlertNames = async () => {
      try {
        setIsLoadingAlertNames(true);
        const filterConfig = {
          key: "alertDescription",
          type: "dynamic",
          label: "Alert Name",
          staticData: [],
          displayType: "multiSelectDropdown",
          queryParams: { table: "alertMeta", column: "alertDescription" },
        };
        const res = await getFilterData(filterConfig);
        const list = res?.response?.response?.filterData || [];
        setAlertNameOptions(list.map((item) => item.id));
      } catch {
        setAlertNameOptions([]);
      } finally {
        setIsLoadingAlertNames(false);
      }
    };

    fetchAlertNames();
  }, []);

  useEffect(() => {
    const fetchResolutionDurations = async () => {
      try {
        setIsLoadingDurations(true);
        const filterConfig = {
          type: "dynamic",
          queryParams: { table: "resolutionDurationBucket", column: "durationBucket", keyColumn: "id" },
        };
        const res = await getFilterData(filterConfig);
        const list = res?.response?.response?.filterData || [];
        setResolutionDurationOptions(list.map((d) => d.value));
      } catch {
        setResolutionDurationOptions([]);
      } finally {
        setIsLoadingDurations(false);
      }
    };

    if (chartType === CHART_TYPES.AUTO_RESOLVED) {
      fetchResolutionDurations();
    }
  }, [chartType]);

  const handleResetFilters = () => {
    setSelectedProjects([]);
    setSelectedEntities([]);
    setSelectedAlertNames([]);
    setSelectedCellChemistries([]);
    setSelectedResolutionDurations([]);
    setSelectedCategories(initialFiltersRef.current.alertCategories);
    setSelectedOperationalStatus(initialFiltersRef.current.operationalStatus);
    if (onHierarchicalLocationChange) onHierarchicalLocationChange(initialFiltersRef.current.hierarchicalLocation);
    if (onFleetIdsChange) onFleetIdsChange(initialFiltersRef.current.fleetIds);
    if (onAlertCategoriesChange) onAlertCategoriesChange(initialFiltersRef.current.alertCategories);
    if (onOperationalStatusChange) onOperationalStatusChange(initialFiltersRef.current.operationalStatus);
    if (onFiltersChange) onFiltersChange([]);
  };

  useEffect(() => {
    const filters = [];
    if (selectedProjects.length > 0) filters.push({ name: "configName", values: selectedProjects });
    if (selectedEntities.length > 0) filters.push({ name: "hierarchy", values: selectedEntities });
    if (selectedAlertNames.length > 0) filters.push({ name: "alertDescription", values: selectedAlertNames });
    if (selectedCellChemistries.length > 0) filters.push({ name: "cellChemistry", values: selectedCellChemistries });
    if (selectedResolutionDurations.length > 0) filters.push({ name: "resolutionDuration", values: selectedResolutionDurations });
    if (!filtersEffectMounted.current) {
      filtersEffectMounted.current = true;
      if (filters.length === 0) return;
    }
    if (onFiltersChange) onFiltersChange(filters);
  }, [selectedProjects, selectedEntities, selectedAlertNames, selectedCellChemistries, selectedResolutionDurations, onFiltersChange]);

  return (
    <>
      {/* Collapse/expand toggle */}
      <IconButton
        onClick={onToggle}
        sx={{
          position: "absolute",
          right: isExpanded ? "320px" : "0",
          zIndex: 1000,
          backgroundColor: "transparent",
          width: "20px",
          height: "20px",
          transition: "right 0.3s ease-in-out",
          boxShadow: "none",
          "&:hover": { backgroundColor: "transparent" },
          "&:focus": { backgroundColor: "transparent" },
        }}
      >
        <PrimaryTooltip title={isExpanded ? "Collapse Panel" : "Expand Panel"} placement="bottom">
          <KeyboardTabIcon
            sx={{
              fontSize: 20,
              color: "#494949",
              transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
              "&:hover": { color: "#6D72F6" },
            }}
          />
        </PrimaryTooltip>
      </IconButton>

      {/* Sidebar panel */}
      <Box
        sx={{
          width: isExpanded ? "320px" : "0px",
          transition: "width 0.3s ease-in-out",
          borderLeft: isExpanded ? "1px solid #E6E6E6" : "none",
          backgroundColor: "#FFFFFF",
          height: "100%",
          overflow: "hidden",
          flexShrink: 0,
          my: -2,
          mr: -3,
          position: "fixed",
          right: "24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "320px",
            opacity: isExpanded ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
          }}
        >
          {/* Settings tab header */}
          <Box sx={{ px: 3, flexShrink: 0 }}>
            <Box sx={{ mx: -3 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                  minHeight: "40px",
                  "& .MuiTab-root": {
                    minHeight: "40px",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#8D8D8D",
                    p: 2,
                    "&.Mui-selected": { color: "#494949" },
                  },
                  "& .MuiTabs-indicator": { backgroundColor: "#494949" },
                }}
              >
                <Tab label="Settings" />
              </Tabs>
            </Box>
            <Divider sx={{ borderColor: "#E6E6E6", mt: 0, mx: -3 }} />
          </Box>

          {/* Scrollable content */}
          <Box sx={{ flex: 1, overflowY: "scroll", pb: 3, "&::-webkit-scrollbar": { width: "6px" } }}>
            {activeTab === 0 && (
              <Box>
                {/* Time Configuration */}
                <Accordion
                  defaultExpanded
                  sx={{
                    boxShadow: "none",
                    borderBottom: "1px solid #E6E6E6",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": { mt: 0, mb: 0 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<KeyboardDoubleArrowDownIcon />}
                    sx={{
                      "&.Mui-expanded": { minHeight: "auto" },
                      "& .MuiAccordionSummary-content": { margin: "20px 0" },
                    }}
                  >
                    <Grid2 container alignItems="center" gap={1.5}>
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", borderRadius: "4px", backgroundColor: "#EFF0FF", p: 0.75 }}>
                        <CustomIcon name="round-clock-icon" width="15" height="15" stroke="#4D44D8" />
                      </Box>
                      <Typography variant="fw700s14c3D4B5A" sx={{ color: "#2E2E2E", lineHeight: "20px" }}>
                        Time Configuration
                      </Typography>
                    </Grid2>
                  </AccordionSummary>
                  <AccordionDetails sx={{ paddingTop: 0 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <Typography variant="fw500s12lh24c686868" sx={{ mb: 1, fontSize: "13px", lineHeight: "20px" }}>
                          Date:
                        </Typography>
                        <ChartCalendarV2
                          startDate={startDate}
                          endDate={endDate}
                          onDateChange={onDateChange}
                          disableAfterDate={analyticsCalendarEndDate()}
                        />
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <Typography variant="fw500s12lh24c686868" sx={{ mb: 1, fontSize: "13px", lineHeight: "20px" }}>
                          Granularity:
                        </Typography>
                        <Grid2 container sx={{ background: "#F9F9F9", padding: "4px", borderRadius: "6px" }}>
                          {GRANULARITY_OPTIONS.map((option) => (
                            <PrimaryGranularityButton
                              key={option}
                              onClick={() => onGranularityChange(option)}
                              active={granularity === option}
                              sx={{ width: "90px" }}
                            >
                              <Typography textTransform="capitalize">{option}</Typography>
                            </PrimaryGranularityButton>
                          ))}
                        </Grid2>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* Filters */}
                <Accordion
                  defaultExpanded
                  sx={{
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": { mt: 0, pb: 3 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<KeyboardDoubleArrowDownIcon />}
                    sx={{
                      "&.Mui-expanded": { minHeight: "auto" },
                      "& .MuiAccordionSummary-content": { margin: "20px 0" },
                    }}
                  >
                    <Grid2 container alignItems="center" justifyContent="space-between" sx={{ width: "100%", pr: 1 }}>
                      <Grid2 container alignItems="center" gap={1.5}>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", borderRadius: "4px", backgroundColor: "#EFF0FF", p: 0.75 }}>
                          <FilterIconSvg sx={{ color: "#4D44D8" }} />
                        </Box>
                        <Typography variant="fw700s14c3D4B5A" sx={{ color: "#2E2E2E", lineHeight: "20px" }}>
                          Filters
                        </Typography>
                      </Grid2>
                      <Typography
                        variant="fw500s12lh246D72F6"
                        onClick={(e) => { e.stopPropagation(); handleResetFilters(); }}
                        sx={{ cursor: "pointer", "&:hover": { color: "#4D44D8" } }}
                      >
                        Reset
                      </Typography>
                    </Grid2>
                  </AccordionSummary>
                  <AccordionDetails sx={{ paddingTop: 0 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                      {canUseGlobalAdvancedFilter && globalFilterConfig ? (
                        <GlobalFilter
                          allFleets={allFleets || []}
                          allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
                          selectedFleetIds={selectedFleetIds || []}
                          selectedLocations={selectedHierarchicalLocation}
                          direction="vertical"
                          onChange={({ fleetIds, hierarchicalLocation }) => {
                            if (onHierarchicalLocationChange) onHierarchicalLocationChange(hierarchicalLocation);
                            // Business Segment and Customer Account both travel as fleetIds,
                            // so dropping it here would leave those two panels inert.
                            if (onFleetIdsChange) onFleetIdsChange(fleetIds);
                          }}
                        />
                      ) : (
                        <LocationFilter
                          locations={allOnboardingLocationsHierarchy}
                          selectedLocations={selectedHierarchicalLocation || []}
                          onLocationChange={(location) => {
                            if (onHierarchicalLocationChange) onHierarchicalLocationChange(location);
                          }}
                        />
                      )}
                      <ProjectFilter
                        selectedProjects={selectedProjects}
                        onProjectChange={setSelectedProjects}
                      />
                      <SimpleCheckboxFilter
                        label="Alert Category"
                        options={alertCategoryOptions}
                        isLoading={isLoadingCategories}
                        selectedValues={selectedCategories}
                        onChange={(categories) => {
                          setSelectedCategories(categories);
                          if (onAlertCategoriesChange) onAlertCategoriesChange(categories);
                        }}
                      />
                      <SimpleCheckboxFilter
                        label="Alert Name"
                        options={alertNameOptions}
                        isLoading={isLoadingAlertNames}
                        selectedValues={selectedAlertNames}
                        onChange={setSelectedAlertNames}
                      />
                      <CellChemistryFilter
                        selectedCellChemistries={selectedCellChemistries}
                        onCellChemistryChange={setSelectedCellChemistries}
                      />
                      {chartType === CHART_TYPES.AUTO_RESOLVED && (
                        <SimpleCheckboxFilter
                          label="Resolution Duration"
                          options={resolutionDurationOptions}
                          isLoading={isLoadingDurations}
                          selectedValues={selectedResolutionDurations}
                          onChange={setSelectedResolutionDurations}
                        />
                      )}
                      <EntityAffectedFilter
                        selectedEntities={selectedEntities}
                        onEntityChange={setSelectedEntities}
                      />
                      <OperationalStatusFilter
                        selectedOperationalStatus={selectedOperationalStatus}
                        onOperationalStatusChange={(status) => {
                          setSelectedOperationalStatus(status);
                          if (onOperationalStatusChange) onOperationalStatusChange(status);
                        }}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SmartAlertsDetailsSidebar;
